import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import { useEffect } from 'react';
import { setExperimentEligibility } from '../../actions/ExperimentationActions';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { isAppAuthSelector } from '../../selectors/AuthSelectors';
import { areExperimentsLoadingSelector, experimentByTreatmentKeySelector, hasExperimentsFetchSucceededSelector } from '../../selectors/ExperimentSelectors';
import { productsFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { laboratoryClient } from '../laboratoryClient';
import { getIsAcceptanceTest } from '../testUtils';

/**
 * Common eligibility criteria.
 */
export const useCommonEligibilityCriteria = () => {
  const isAppReady = useGlobalLoading();
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const isMobile = useIsMobile();
  const ownedProducts = useAppSelector(state => {
    if (!isAppReady) return [];
    return productsFromProductInventorySelector(state);
  });
  const isFreePortal = ownedProducts && ownedProducts.length <= 0;
  return {
    isAppReady,
    isAppAuth,
    isMobile,
    isFreePortal
  };
};

/**
 * Log experiment exposure
 */
export const logExperimentExposure = treatmentKey => {
  laboratoryClient.logExposure(treatmentKey);
};

/**
 * Get experiment data
 *
 * @param treatmentKey - The treatment key for the experiment
 * @param groupParameterKey - The group parameter key for the experiment, defaults to 'group'
 * @param groupParameterOptions - The group parameter options for the experiment
 * @param experimentEligibilityCondition - The eligibility condition for the experiment, defaults to a condition that is always met
 */
export const useExperiment = ({
  treatmentKey,
  groupParameterKey = 'group',
  groupParameterOptions,
  experimentEligibilityCondition = () => ({
    isConditionMet: true,
    eligibilityConditionData: {}
  })
}) => {
  const experiment = useAppSelector(state => experimentByTreatmentKeySelector(state, treatmentKey));

  // Loading statuses
  const areExperimentsLoading = useAppSelector(areExperimentsLoadingSelector);
  const hasExperimentsFetchSucceeded = useAppSelector(hasExperimentsFetchSucceededSelector);

  // Eligibility criteria
  const isAcceptanceTest = getIsAcceptanceTest();
  const {
    isConditionMet,
    eligibilityConditionData
  } = experimentEligibilityCondition();
  const isEligible = hasExperimentsFetchSucceeded && !isAcceptanceTest && isConditionMet;
  const allCapsTreatmentKey = treatmentKey.split('-').join('').toUpperCase();
  const groupParameterOptionsObject = {};
  for (const option of Object.keys(groupParameterOptions)) {
    const capitalizedOption = option.charAt(0).toUpperCase() + option.slice(1);
    const experimentCohortKey = `is${allCapsTreatmentKey}${capitalizedOption}`;
    groupParameterOptionsObject[experimentCohortKey] = isEligible && groupParameterOptions[option] === (experiment === null || experiment === void 0 ? void 0 : experiment.parameters[groupParameterKey]);
  }
  return Object.assign({
    // experiment data
    [`isEligibleFor${allCapsTreatmentKey}`]: isEligible,
    [`is${allCapsTreatmentKey}Loading`]: areExperimentsLoading,
    [`is${allCapsTreatmentKey}Fetched`]: hasExperimentsFetchSucceeded
  }, groupParameterOptionsObject, eligibilityConditionData);
};

/**
 * Use this hook to log exposure, update redux state, and return experiment data
 *
 * @param treatmentKey - The treatment key for the experiment
 * @param experimentEligibilityCondition - The eligibility condition for the experiment, defaults to a condition that is always met
 * @param groupParameterOptions - The group parameter options for the experiment
 * @param groupParameterKey - The group parameter key for the experiment, defaults to 'group'
 * @param shouldLogExposure - Whether to log exposure for the experiment, defaults to true
 */
export const useExperimentEffect = ({
  treatmentKey,
  experimentEligibilityCondition = () => {
    return {
      isConditionMet: true,
      eligibilityConditionData: {}
    };
  },
  groupParameterOptions,
  groupParameterKey = 'group',
  shouldLogExposure = true
}) => {
  const isInApp = useAppSelector(isAppAuthSelector);
  const isPublic = !isInApp;
  const experiment = useExperiment({
    treatmentKey,
    experimentEligibilityCondition,
    groupParameterOptions,
    groupParameterKey
  });
  const dispatch = useAppDispatch();
  const allCapsTreatmentKey = treatmentKey.split(/[-_]/).join('').toUpperCase();
  const isEligible = experiment[`isEligibleFor${allCapsTreatmentKey}`];
  const whichGroup = Object.keys(groupParameterOptions).find(key => {
    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
    return experiment[`is${allCapsTreatmentKey}${capitalizedKey}`];
  }) || 'control';
  useEffect(() => {
    if (isEligible) {
      if (shouldLogExposure) {
        logExperimentExposure(treatmentKey);
      }
      dispatch(setExperimentEligibility({
        treatmentKey,
        isEligible: true,
        group: whichGroup,
        isPublic,
        isInApp
      }));
    }
  }, [dispatch, isEligible, treatmentKey, whichGroup, shouldLogExposure, isInApp, isPublic]);
  return experiment;
};